import React, { useEffect, useMemo, useState } from 'react'
import { ToastContainer } from 'react-toastify'
import './Drawer.scss'

interface Tab {
  key: string;
  label: string;
  content: React.ReactNode;
}

interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children?: React.ReactNode;
  position?: 'left' | 'right' | 'center';
  /** Max-width for centred mode. Side-mode defaults to 50vw on tablet+. */
  maxWidth?: string;
  closeOnOutsideClick?: boolean;
  showTabs?: boolean;
  tabs?: Tab[];
  defaultTab?: string;
  footer?: React.ReactNode;
}

// Tailwind-style alias support kept for parity with the insurance-ui Drawer; lets
// callers pass `max-w-2xl` instead of `42rem`.
const TAILWIND_WIDTHS: Record<string, string> = {
  'max-w-xs': '20rem',
  'max-w-sm': '24rem',
  'max-w-md': '28rem',
  'max-w-lg': '32rem',
  'max-w-xl': '36rem',
  'max-w-2xl': '42rem',
  'max-w-3xl': '48rem',
  'max-w-4xl': '56rem',
  'max-w-5xl': '64rem',
  'max-w-6xl': '72rem',
  'max-w-7xl': '80rem'
}

const resolveMaxWidth = (value: string): string => {
  const trimmed = value.trim()
  return TAILWIND_WIDTHS[trimmed] ?? trimmed
}

const Drawer: React.FC<DrawerProps> = ({
  isOpen,
  onClose,
  title,
  children,
  position = 'right',
  maxWidth = '42rem',
  closeOnOutsideClick = false,
  showTabs = false,
  tabs,
  defaultTab,
  footer
}) => {
  const [visible, setVisible] = useState(false)
  const [activeTab, setActiveTab] = useState(defaultTab || '')

  // Keep the panel mounted until the slide-out keyframe finishes before
  // unmounting. Must match $drawer-duration (320ms) in Drawer.scss.
  useEffect(() => {
    if (isOpen) {
      setVisible(true)
      document.body.classList.add('drawer-open')
      return () => document.body.classList.remove('drawer-open')
    }
    const timer = setTimeout(() => setVisible(false), 320)
    return () => {
      clearTimeout(timer)
      document.body.classList.remove('drawer-open')
    }
  }, [isOpen])

  useEffect(() => {
    if (tabs && tabs.length > 0) {
      setActiveTab(defaultTab || tabs[0].key)
    }
  }, [tabs, defaultTab])

  const resolvedMaxWidth = useMemo(() => resolveMaxWidth(maxWidth), [maxWidth])

  if (!visible && !isOpen) return null

  let positionClass = 'drawer__panel--right'
  if (position === 'left') positionClass = 'drawer__panel--left'
  if (position === 'center') positionClass = 'drawer__panel--center'

  const widthClass =
    position === 'center'
      ? 'drawer__panel--centerWidth'
      : 'drawer__panel--sideWidth'

  let animationClass = 'drawer__panel--slideInRight'
  if (position === 'left') animationClass = 'drawer__panel--slideInLeft'
  if (position === 'center') animationClass = 'drawer__panel--fadeIn'

  if (!isOpen) {
    animationClass = 'drawer__panel--slideOutRight'
    if (position === 'left') animationClass = 'drawer__panel--slideOutLeft'
    if (position === 'center') animationClass = 'drawer__panel--fadeOut'
  }

  const panelStyle = {
    '--drawer-max-width': resolvedMaxWidth
  } as React.CSSProperties

  const renderTabs = () => {
    if (!showTabs || !tabs || tabs.length === 0) return null
    return (
      <div className="drawer__tabs">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            className={`drawer__tab ${activeTab === tab.key ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
            type="button"
          >
            {tab.label}
          </button>
        ))}
      </div>
    )
  }

  const getActiveTabContent = () => {
    if (!showTabs || !tabs || tabs.length === 0) return null
    const activeTabData = tabs.find((tab) => tab.key === activeTab)
    return activeTabData?.content || null
  }

  return (
    <div className={`drawer ${isOpen ? 'drawer--open' : 'drawer--closed'}`}>
      <button
        type="button"
        className="drawer__overlay"
        onClick={closeOnOutsideClick ? onClose : undefined}
        aria-label="Close drawer"
      />
      <div
        className={`drawer__panel ${positionClass} ${widthClass} ${animationClass}`}
        style={panelStyle}
      >
        <div className="drawer__header">
          <h2 className="drawer__title">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="drawer__closeBtn"
            aria-label="Close drawer"
          >
            <svg
              className="drawer__closeIcon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
        {renderTabs()}
        <div className="drawer__body">
          {showTabs && tabs ? getActiveTabContent() : children}
        </div>
        {footer && <div className="drawer__footer">{footer}</div>}
        <ToastContainer containerId="drawerToast" />
      </div>
    </div>
  )
}

export default Drawer
